'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { SidePanel } from '@/shared/ui/SidePanelWrapper';
import { FourCsWidgetSchemaType } from '@inference.engine/widgets';
import { getWidgetStores } from '@/entities/widget';
import { useAgentCategoryChipColor } from '@/entities/agent';
import { ErrorState } from './components/ErrorState';
import { FourCs } from './components/FourCs';
import { Truths } from './components/Truths';

import { FOUR_CS_KEYS } from '../model/four-cs-widget.constants';
import { Spinner } from '@monksflow/monks-ui';
import { ArrowRightIcon } from 'lucide-react';
import { CenteredContent } from '@/shared/ui/layout/CenteredContent';
import { useTranslation } from 'react-i18next';

type CurrentTab = 'fourcs' | 'truths';

function hasData(c: {
  guidingQuestions: string[];
  findings: string[];
  implications: unknown[];
  truths: unknown[];
}): boolean {
  return (
    c.guidingQuestions.length > 0 ||
    c.findings.length > 0 ||
    c.implications.length > 0 ||
    c.truths.length > 0
  );
}

function getInitialSelectedTruths(data: FourCsWidgetSchemaType): Set<string> {
  const selected = new Set<string>();
  FOUR_CS_KEYS.forEach(({ key }) => {
    data.fourCs[key].truths.forEach((truth) => {
      if (truth.recommended) selected.add(truth.truth);
    });
  });
  return selected;
}

export const FourCsWidget = ({ data }: { data: FourCsWidgetSchemaType }) => {
  const { t } = useTranslation('analysis');

  // Store access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`.
  const { widgetStore, chatStore } = getWidgetStores();
  const widget = widgetStore.useWidget();
  const { sendMessage } = chatStore.useChatActions();
  const error = widgetStore.useWidgetError();
  if (!widget) {
    throw new Error('Widget not found');
  }

  const [currentTab, setCurrentTab] = useState<CurrentTab>('fourcs');
  const allCsComplete = useMemo(() => {
    const { category, consumer, company, cultural } = data.fourCs;
    return hasData(category) && hasData(consumer) && hasData(company) && hasData(cultural);
  }, [data.fourCs]);
  const initialSelected = useMemo(() => getInitialSelectedTruths(data), [data]);
  const [selectedTruths, setSelectedTruths] = useState<Set<string>>(initialSelected);

  // Sync selectedTruths with initialSelected when data changes (incremental loading)
  useEffect(() => {
    setSelectedTruths(initialSelected);
  }, [initialSelected]);

  const { chatId } = useParams<{ chatId: string }>();
  // Fetch the chat title from the header
  const { data: chatInfo } = useQuery({
    queryKey: ['chatInfo', chatId],
    queryFn: () => chatStore.getChatInfo({ contextId: chatId! }),
    enabled: !!chatId,
  });
  const chatTitle = chatInfo?.title || t('fourCsWidget.fourCs.reviewThe4CS');

  const handleCreateTerritories = useCallback(() => {
    const truthsList = Array.from(selectedTruths)
      .map((truth) => `- ${truth}`)
      .join('\n');

    void sendMessage({
      agentId: widget.agentName,
      prompt: t('fourCsWidget.createTerritoriesForTheFollowing', { truthsList }),
    });
  }, [selectedTruths, sendMessage, widget.agentName, t]);

  const categoryChipColor = useAgentCategoryChipColor(widget.sourceAgentId ?? widget.agentName);

  const headerConfig = useMemo(() => {
    if (currentTab === 'truths') {
      return {
        backButton: {
          label: t('fourCsWidget.back'),
          onClick: () => setCurrentTab('fourcs'),
        },
        actions: [
          {
            type: 'button' as const,
            label: t('fourCsWidget.createTerritories'),
            onClick: handleCreateTerritories,
            variant: 'contained' as const,
          },
        ],
      };
    } else {
      return {
        chip: { label: t('fourCsWidget.createStrategy'), color: categoryChipColor },
        actions: [
          {
            type: 'button' as const,
            label: allCsComplete
              ? t('fourCsWidget.continueToSelectTruths')
              : t('fourCsWidget.loading'),
            onClick: () => setCurrentTab('truths'),
            variant: 'contained' as const,
            disabled: !allCsComplete,
            icon: allCsComplete ? <ArrowRightIcon /> : <Spinner aria-hidden />,
            iconPosition: 'end' as const,
          },
        ],
      };
    }
  }, [categoryChipColor, currentTab, handleCreateTerritories, allCsComplete, t]);

  // Render error state first, before checking isEmpty
  if (error) {
    return (
      <ErrorState
        chatTitle={chatTitle}
        error={error || t('fourCsWidget.anErrorOccurredWhileGenerating')}
        categoryChipColor={categoryChipColor}
      />
    );
  }

  const isEmpty =
    !hasData(data.fourCs.category) &&
    !hasData(data.fourCs.consumer) &&
    !hasData(data.fourCs.company) &&
    !hasData(data.fourCs.cultural);

  if (isEmpty) {
    return (
      <SidePanel
        title={chatTitle}
        headerConfig={{
          chip: { label: t('fourCsWidget.createStrategy'), color: categoryChipColor },
        }}
      >
        <CenteredContent className="px-10 pb-10">
          <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
            <Spinner className="size-10" />
            <p className="text-heading-lg font-weight-medium text-foreground">
              {t('fourCsWidget.generatingFourCSResearch')}
            </p>
            <p className="text-body-lg text-muted-foreground">
              {t('fourCsWidget.buildingEachCSequentiallyCategory')}
            </p>
          </div>
        </CenteredContent>
      </SidePanel>
    );
  }

  return (
    <SidePanel title={currentTab === 'truths' ? '' : chatTitle} headerConfig={headerConfig}>
      {currentTab === 'fourcs' && <FourCs data={data} />}
      {currentTab === 'truths' && (
        <Truths
          data={data}
          chatTitle={chatTitle}
          selectedTruths={selectedTruths}
          setSelectedTruths={setSelectedTruths}
        />
      )}
    </SidePanel>
  );
};
